import { useState, useRef, useEffect } from 'react';
import { generatePath, NavLink } from 'react-router-dom';
import { PATHS } from '../../../../constants/routes';
import ActionLinkButton from '../../../UI/ActionLinkButton/ActionLinkButton';
import { Category } from '../../../../types/common';
import './menu.scss';

interface MenuProps {
    scrolled: boolean;
    categories: Category[];
    isMobile?: boolean;
    isAuthenticated: boolean;
    openAuthPopup: any;
}

const Menu: React.FC<MenuProps> = ({ scrolled, categories, isMobile, isAuthenticated, openAuthPopup }) => {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const scrollYRef = useRef<number>(0);
    const lockedRef = useRef<boolean>(false);

    const toggle = () => setIsOpen((v) => !v);

    useEffect(() => {
        if (!isOpen || isMobile) return;
        const onClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;
            if (containerRef.current && !containerRef.current.contains(target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => {
            document.removeEventListener('mousedown', onClickOutside);
        };
    }, [isOpen, isMobile]);

    // блокировка/разблокировка прокрутки для мобильных при открытом меню
    useEffect(() => {
        if (typeof window === 'undefined' || typeof document === 'undefined') return;
        const body = document.body;
        const html = document.documentElement;
        const preventScroll = (e: Event) => {
            const target = e.target as Node;
            if (menuRef.current && menuRef.current.contains(target)) {
                return;
            }
            e.preventDefault();
        };
        const lockScroll = () => {
            if (lockedRef.current) return;
            scrollYRef.current = window.scrollY || window.pageYOffset || 0;
            html.style.overflow = 'hidden';
            body.style.overflow = 'hidden';
            (body.style as any).touchAction = 'none';
            (html.style as any).touchAction = 'none';
            document.addEventListener('touchmove', preventScroll, { passive: false });
            document.addEventListener('wheel', preventScroll, { passive: false } as any);
            lockedRef.current = true;
        };
        const unlockScroll = () => {
            if (!lockedRef.current) return;
            html.style.removeProperty('overflow');
            body.style.removeProperty('overflow');
            (body.style as any).touchAction = '';
            (html.style as any).touchAction = '';
            document.removeEventListener('touchmove', preventScroll as any);
            document.removeEventListener('wheel', preventScroll as any);
            lockedRef.current = false;
            window.scrollTo(0, scrollYRef.current || 0);
        };
        if (isMobile && isOpen) {
            lockScroll();
        } else {
            unlockScroll();
        }
        return () => {
            if (lockedRef.current) {
                unlockScroll();
            }
        };
    }, [isMobile, isOpen]);

    const getNavItemClassName = ({ isActive }: { isActive: boolean }) => `header-menu__nav_elem${isActive ? ' header-menu__nav_elem_active' : ''}`;

    return (
        <div ref={containerRef} className="header-menu">
            <ActionLinkButton
                className={`header-menu__button ${isOpen ? 'header-menu__button_open' : ''}`}
                ariaLabel={isOpen ? 'Закрыть меню' : 'Открыть меню'}
                onClick={toggle}
                iconSrc={isOpen ? '/icons/new/close_black.svg' : '/icons/new/menu.svg'}
                iconSrcDark={isOpen ? '/icons/mobile/close_w.svg' : '/icons/mobile/menu_w.svg'}
            />
            <div className={`header-menu__content ${isOpen ? 'header-menu__content_open' : ''}`}>
                <div className="header-menu__content_box">
                    <nav
                        id="site-navigation"
                        ref={menuRef}
                        className={['header-menu__nav', scrolled ? 'header-menu__nav_scrolled' : '', isMobile ? 'header-menu__nav_mobile' : '']
                            .filter(Boolean)
                            .join(' ')}
                        aria-label="Основное меню"
                    >
                        <div className="header-menu__nav_container">
                            {categories &&
                                categories.map((category, index) => {
                                    return (
                                        <NavLink
                                            key={`category-${index}`}
                                            className={getNavItemClassName}
                                            to={generatePath('/catalog/:url', { url: category.url })}
                                            onClick={() => setIsOpen(false)}
                                        >
                                            {category.name}
                                        </NavLink>
                                    );
                                })}
                            <NavLink className="header-menu__nav_elem" to={PATHS.showcase} onClick={() => setIsOpen(false)}>
                                Все товары
                            </NavLink>
                            <NavLink className="header-menu__nav_elem" to={PATHS.accessories} onClick={() => setIsOpen(false)}>
                                Идея для подарков
                            </NavLink>
                            <NavLink className={getNavItemClassName} to={PATHS.faq} onClick={() => setIsOpen(false)}>
                                Вопросы и ответы
                            </NavLink>
                        </div>

                        <div className="header-menu__nav_container">
                            <NavLink className={getNavItemClassName} to={PATHS.aboutCompany} onClick={() => setIsOpen(false)}>
                                О компании
                            </NavLink>
                            <NavLink className={getNavItemClassName} to={PATHS.deliveryPayment} onClick={() => setIsOpen(false)}>
                                Доставка и оплата
                            </NavLink>
                            <NavLink className={getNavItemClassName} to={PATHS.loyalty} onClick={() => setIsOpen(false)}>
                                Бонусная программа
                            </NavLink>
                            <NavLink className={getNavItemClassName} to={`${PATHS.showcase}/discounts`} onClick={() => setIsOpen(false)}>
                                Акции
                            </NavLink>
                            <NavLink className={getNavItemClassName} to={PATHS.hotelProducts} onClick={() => setIsOpen(false)}>
                                Партнерам
                            </NavLink>
                            <NavLink className={getNavItemClassName} to={PATHS.customers} onClick={() => setIsOpen(false)}>
                                Покупателям
                            </NavLink>
                            {categories &&
                                categories.length > 4 &&
                                (isAuthenticated ? (
                                    <NavLink className={getNavItemClassName} to={PATHS.account} onClick={() => setIsOpen(false)}>
                                        Личный кабинет
                                    </NavLink>
                                ) : (
                                    <button
                                        className="header-menu__nav_elem header-menu__nav_elem_btn"
                                        onClick={() => {
                                            // setIsOpen(false);
                                            openAuthPopup();
                                        }}
                                    >
                                        Личный кабинет
                                    </button>
                                ))}
                            <NavLink className={getNavItemClassName} to={PATHS.contacts} onClick={() => setIsOpen(false)}>
                                Контакты
                            </NavLink>
                        </div>
                    </nav>
                </div>
            </div>
        </div>
    );
};

export default Menu;
